import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import ClustersDnDColumn, { type Item } from "@/components/Board";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  BadgeCheck,
  ChartNoAxesColumnIncreasing,
  Eye,
  Info,
  Package,
  ScanEye,
  Search,
  TriangleAlert,
  X,
  Zap,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  billingAPI,
  visabilityAPI,
  promptsAPI,
  type ClusterAction,
  type PromptCluster,
} from "@/services/api";
import { usePromptsRealtime } from "@/hooks/usePromptsRealtime";
import type { DateRangeValue } from "@/components/DateRangeFilter";
import type { Brand } from "@/components/AppSidebar";
import { useVisibilityActionablesRealtime } from "@/hooks/useVisibilityActionablesRealtime";
import { isQueuedOrRunningPrompt } from "./promptsMetricLoading";
import {
  capturePosthogEvent,
  getTimeSinceBrandOnboardingSeconds,
  isFirstDashboardView,
} from "@/lib/posthog";
import {
  getCooldownBlockedMessage,
  getPromptRunsBlockedMessage,
  getTrialBoostBlockedInfo,
  shouldBlockBoostByCooldown,
} from "@/lib/clusterBoost";
import { resolvePlanType } from "@/lib/planTier";
import { useAuth } from "@/components/AuthContext";
import { toast } from "sonner";
import repeatIcon from "@/assets/repeat.svg";
import "./style.css";

const ALL_CLUSTERS_ID = "all-clusters";
const EMPTY_ACTIONS_REFETCH_INTERVAL_MS = 15000;
const READY_WITHOUT_ACTIONS_GRACE_MS = 2 * 60 * 1000;
const CLUSTER_ORDER_STORAGE_PREFIX = "cluster-actions:order";
const ALLOWED_BOOST_DAYS = new Set([1, 7, 14, 30, 90]);

const resolveBoostDays = (timeRange?: string, fallback = 30): number => {
  const parsed = Number.parseInt(
    String(timeRange || "").replace(/d$/i, ""),
    10,
  );
  if (ALLOWED_BOOST_DAYS.has(parsed)) {
    return parsed;
  }
  return fallback;
};

const toBoardStatus = (value?: string | null) => {
  switch (value) {
    case "inProgress":
      return "In Progress";
    case "resolved":
      return "Completed";
    case "canceled":
      return "Dismissed";
    default:
      return "To do";
  }
};

const toPriority = (value?: string | null) => {
  if (!value) return "medium";
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith("low")) return "low";
  if (normalized.startsWith("high")) return "high";
  return "medium";
};

const toTaskTypeLabel = (value?: string | null) => {
  if (!value) return "Outreach";
  const normalized = value.trim();
  if (normalized === "GuestPost") return "Guest Post";
  if (normalized === "OwnedListicle") return "Owned Listicle";
  if (normalized === "FreePublish") return "Free Publish";
  if (normalized === "YouTubeVideo") return "YouTube Video";
  if (normalized === "LinkedInArticle") return "LinkedIn Article";
  return normalized;
};

const toSourceType = (value?: string | null) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (!normalized) return "aggregator";
  if (normalized.includes("directory") || normalized.includes("listing")) {
    return "directory";
  }
  if (
    normalized.includes("guest") ||
    normalized.includes("article") ||
    normalized.includes("publisher") ||
    normalized.includes("content")
  ) {
    return "article";
  }
  return "aggregator";
};

const toDomainLabel = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    return parsed.hostname.replace(/^www\./i, "");
  } catch {
    return "";
  }
};

const COMMUNITY_TASK_PATTERN =
  /community|discussion|forum|thread|reddit|quora|stack\s*overflow|stackexchange|q\s*&\s*a|\bqa\b|\bqna\b/i;
const COMMUNITY_PATH_PATTERN =
  /\/(r\/[^/]+\/comments|questions?|answers?|qna|forums?|community|communities|discussion|discussions|thread|threads|topic|topics)(\/|$)/i;
const YOUTUBE_HOSTS = ["youtube.com", "youtu.be"];

const normalizeEvidenceLikeUrl = (value?: string | null) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
  return "";
};

const isYouTubeSourceValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalizedUrl = normalizeEvidenceLikeUrl(value);
    if (!normalizedUrl) return /youtube/i.test(value);
    try {
      const host = new URL(normalizedUrl).hostname
        .replace(/^www\./i, "")
        .toLowerCase();
      return YOUTUBE_HOSTS.some(
        (youtubeHost) =>
          host === youtubeHost || host.endsWith(`.${youtubeHost}`),
      );
    } catch {
      return false;
    }
  }
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, any>;
  const urlType = String(entry?.url_type || entry?.urlType || entry?.type || "")
    .trim()
    .toLowerCase();
  if (urlType.includes("youtube")) return true;
  return isYouTubeSourceValue(
    entry?.url || entry?.source_url || entry?.href || entry?.loc || "",
  );
};

const isCommunitySourceValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    const normalizedUrl = normalizeEvidenceLikeUrl(value);
    if (!normalizedUrl) return COMMUNITY_TASK_PATTERN.test(value);
    try {
      const parsed = new URL(normalizedUrl);
      const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
      if (
        host === "reddit.com" ||
        host.endsWith(".reddit.com") ||
        host === "quora.com" ||
        host.endsWith(".quora.com") ||
        host === "stackoverflow.com" ||
        host.endsWith(".stackoverflow.com") ||
        host === "stackexchange.com" ||
        host.endsWith(".stackexchange.com")
      ) {
        return true;
      }
      if (
        host === "github.com" &&
        /\/(issues|discussions?)(\/|$)/i.test(parsed.pathname || "")
      ) {
        return true;
      }
      return COMMUNITY_PATH_PATTERN.test(parsed.pathname || "");
    } catch {
      return false;
    }
  }
  if (!value || typeof value !== "object") return false;
  const entry = value as Record<string, any>;
  const domainType = String(entry?.domain_type || entry?.domainType || "")
    .trim()
    .toLowerCase();
  if (domainType.includes("community")) return true;
  const urlType = String(entry?.url_type || entry?.urlType || entry?.type || "")
    .trim()
    .toLowerCase();
  if (COMMUNITY_TASK_PATTERN.test(urlType)) return true;
  return isCommunitySourceValue(
    entry?.url || entry?.source_url || entry?.href || entry?.loc || "",
  );
};

const sortByStoredClusterOrder = <T extends { id?: string | null }>(
  clusters: T[],
  storageKey: string | null,
): T[] => {
  if (!clusters.length || !storageKey) return clusters;

  try {
    const storedOrderRaw = sessionStorage.getItem(storageKey);
    const parsedOrder = JSON.parse(storedOrderRaw || "[]");
    const storedOrder = Array.isArray(parsedOrder)
      ? (parsedOrder as unknown[])
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      : [];

    const orderMap = new Map<string, number>();
    storedOrder.forEach((id, index) => {
      if (!orderMap.has(id)) orderMap.set(id, index);
    });

    let nextIndex = orderMap.size;
    const nextStoredOrder = [...storedOrder];
    clusters.forEach((cluster) => {
      const id = String(cluster?.id || "").trim();
      if (!id || orderMap.has(id)) return;
      orderMap.set(id, nextIndex++);
      nextStoredOrder.push(id);
    });

    if (nextStoredOrder.length !== storedOrder.length) {
      sessionStorage.setItem(storageKey, JSON.stringify(nextStoredOrder));
    }

    const inputIndexById = new Map<string, number>();
    clusters.forEach((cluster, index) => {
      const id = String(cluster?.id || "").trim();
      if (!id) return;
      if (!inputIndexById.has(id)) inputIndexById.set(id, index);
    });

    return clusters.slice().sort((left, right) => {
      const leftId = String(left?.id || "").trim();
      const rightId = String(right?.id || "").trim();
      const leftOrder = orderMap.get(leftId) ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = orderMap.get(rightId) ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      const leftInputIndex = inputIndexById.get(leftId) ?? 0;
      const rightInputIndex = inputIndexById.get(rightId) ?? 0;
      return leftInputIndex - rightInputIndex;
    });
  } catch {
    return clusters;
  }
};

const BOOST_RUNNING_STATUSES = new Set(["generating", "queued"]);

export const isRunningBoostStatus = (value?: string | null) =>
  BOOST_RUNNING_STATUSES.has(
    String(value || "")
      .trim()
      .toLowerCase(),
  );

export const getRunningClusterIds = (
  clusters?: Array<Pick<PromptCluster, "id" | "boostStatus">> | null,
) => {
  const set = new Set<string>();
  (clusters || []).forEach((cluster) => {
    if (cluster?.id && isRunningBoostStatus(cluster?.boostStatus)) {
      set.add(cluster.id);
    }
  });
  return set;
};

export const resolveBoostRunningState = (
  clusters: Array<Pick<PromptCluster, "id" | "boostStatus">> | null | undefined,
  selectedCluster: string,
) => {
  const runningClusterIds = getRunningClusterIds(clusters);
  const hasAnyBoostRunning = runningClusterIds.size > 0;
  const isSelectedClusterBoostRunning =
    selectedCluster !== ALL_CLUSTERS_ID &&
    runningClusterIds.has(selectedCluster);
  const isBoostRunning =
    selectedCluster === ALL_CLUSTERS_ID
      ? hasAnyBoostRunning
      : isSelectedClusterBoostRunning;
  return {
    runningClusterIds,
    hasAnyBoostRunning,
    isBoostRunning,
  };
};

const UUID_PATTERN =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

const normalizeGeneratedCopy = (
  value?: string | null,
  options?: { stripPromptIds?: boolean },
) => {
  let text = String(value || "").trim();
  if (!text) return "";

  if (options?.stripPromptIds) {
    text = text.replace(/\bprompt\s+[0-9a-f-]{8,}\b/gi, "this prompt");
    text = text.replace(UUID_PATTERN, "");
  }

  text = text
    .replace(/,(\s*,)+/g, ", ")
    .replace(/\s+,/g, ",")
    .replace(/,\s+\./g, ".")
    .replace(/sources,\s*(citation count)/gi, "sources. $1")
    .replace(/([^.|\n])\s*(\|\s*URL\s*\|)/gi, "$1\n$2")
    .replace(/([.!?])\s+(?=[A-Z])/g, "$1\n")
    .replace(/\s{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return text;
};

const getPromptMetric = (prompt: any, key: "visibility" | "position") => {
  if (prompt && typeof prompt === "object" && key in prompt) {
    return (prompt as any)[key];
  }
  return undefined;
};

const averageMetric = (values: Array<number | null | undefined>) => {
  const valid = values.filter((value) => typeof value === "number") as number[];
  if (!valid.length) return null;
  return valid.reduce((acc, value) => acc + value, 0) / valid.length;
};

const formatAvgMetric = (value: number | null, suffix = "") => {
  if (value === null || !Number.isFinite(value)) return "--";
  const rounded = Math.round(value * 10) / 10;
  return `${rounded}${suffix}`;
};

const formatBoostedAtLabel = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const toTimestamp = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
};
export { getTrialBoostBlockedInfo };
export { shouldBlockBoostByCooldown };

export const PromptsActionPage = () => {
  const { locale, t } = useLocale();
  const { user } = useAuth();
  const [search, setSearch] = React.useState("");
  const [searchParams] = useSearchParams();
  const clusterFromSearch = searchParams.get("cluster");
  const [selectedCluster, setSelectedCluster] = useState(
    clusterFromSearch || ALL_CLUSTERS_ID,
  );
  const [isBoostSubmitting, setIsBoostSubmitting] = useState(false);
  const [isBoostLimitModalOpen, setIsBoostLimitModalOpen] = useState(false);
  const {
    clusterFilters,
    currentBrand,
    selectedCountries,
    selectedProviders,
    timeRange,
    dateRange,
  } = useOutletContext<{
    clusterFilters: any;
    currentBrand: Brand;
    selectedCountries: string[];
    selectedProviders?: string[];
    timeRange?: string;
    dateRange?: DateRangeValue;
  }>();
  const resolvedBrandId =
    (currentBrand as any)?.brandId || (currentBrand as any)?.id;
  const navigate = useNavigate();
  const resolvedDays = resolveBoostDays(timeRange, 30);
  const promptDateRange =
    dateRange?.preset === "custom" && dateRange.startDate && dateRange.endDate
      ? {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
        }
      : null;
  const promptDateRangeKey = promptDateRange
    ? `${promptDateRange.startDate}:${promptDateRange.endDate}`
    : "rolling";
  const providersKey = selectedProviders?.join(",") || "all";
  const promptsQueryKey = [
    "prompts",
    resolvedBrandId,
    selectedCountries,
    providersKey,
    resolvedDays,
    promptDateRangeKey,
  ];
  const actionsQueryKey = [
    "get-cited-actions",
    resolvedBrandId,
    resolvedDays,
    providersKey,
    locale,
  ];
  const resolvedOrgId =
    ((user as any)?.company?.id as string | undefined) ||
    ((user as any)?.companyId as string | undefined) ||
    null;
  const { data: billingSubscription, isFetched: billingSubscriptionFetched } =
    useQuery({
      queryKey: ["billing-subscription", (user as any)?.id || "unknown"],
      queryFn: () => billingAPI.getSubscription(),
      enabled: Boolean((user as any)?.id),
      staleTime: 60000,
    });
  const resolvedPlanType = resolvePlanType(
    user?.tier,
    billingSubscription?.subscription_status,
  );
  const normalizedUserTier = String((user as any)?.tier || "")
    .trim()
    .toLowerCase();
  const isStarterTierToken =
    normalizedUserTier === "1" || normalizedUserTier === "starter";
  const planType =
    isStarterTierToken && !billingSubscriptionFetched
      ? "paid"
      : resolvedPlanType;
  const boostEventSource = "get_cited" as const;
  const queryClient = useQueryClient();
  const trackedPageViewedRef = useRef<string>("");
  const trackedGetCitedViewRef = useRef<string>("");
  const trackedDashboardViewRef = useRef<string>("");
  const selectedClusterStorageKey = useMemo(
    () => `cluster-actions:selected-cluster:${resolvedBrandId || "no-brand"}`,
    [resolvedBrandId],
  );
  const clusterOrderStorageKey = useMemo(
    () =>
      resolvedBrandId
        ? `${CLUSTER_ORDER_STORAGE_PREFIX}:${resolvedBrandId}`
        : null,
    [resolvedBrandId],
  );

  const mapStatusToApi = (value: string) => {
    switch (value) {
      case "To do":
        return "toAddress";
      case "In Progress":
        return "inProgress";
      case "Completed":
        return "resolved";
      case "Dismissed":
        return "canceled";
      default:
        return null;
    }
  };

  const mapPriorityToApi = (value: string) => {
    switch (value) {
      case "low":
        return "Low";
      case "medium":
        return "Medium";
      case "high":
        return "High";
      default:
        return null;
    }
  };

  const toActionType = (value?: string | null) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (normalized.includes("guest")) return "guest_post";
    if (normalized.includes("wiki")) return "wikipedia";
    if (normalized.includes("directory") || normalized.includes("listing")) {
      return "directory";
    }
    return "outreach";
  };

  const toStatusValue = (value?: string | null) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    if (normalized === "to do" || normalized === "toaddress") return "todo";
    if (normalized === "in progress" || normalized === "inprogress") {
      return "in_progress";
    }
    if (normalized === "completed" || normalized === "resolved") return "done";
    return "dismissed";
  };

  const {
    data: actionablesData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: actionsQueryKey,
    queryFn: async () => {
      if (!resolvedBrandId || !currentBrand)
        return { actionables: [], clusterActions: [] };
      const res = await visabilityAPI.getActionables(
        { ...currentBrand, brandId: resolvedBrandId },
        resolvedDays,
        selectedProviders,
      );
      return res.data;
    },
    enabled: !!resolvedBrandId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchInterval: (query) => {
      if (query.state.error) return false;
      const data = query.state.data as
        | { clusterActions?: Array<unknown> }
        | undefined;
      const hasClusterActions =
        Array.isArray(data?.clusterActions) && data.clusterActions.length > 0;
      return hasClusterActions ? false : EMPTY_ACTIONS_REFETCH_INTERVAL_MS;
    },
    refetchIntervalInBackground: false,
  });

  useVisibilityActionablesRealtime({
    brandId: resolvedBrandId,
    days: resolvedDays,
    providers: selectedProviders,
    queryKey: actionsQueryKey,
    enabled: !!resolvedBrandId,
    onUpdate: () => {
      if (!resolvedBrandId) return;
      queryClient.invalidateQueries({
        queryKey: promptsQueryKey,
      });
    },
  });

  const {
    data: promptsData,
    isLoading: isPromptsLoading,
    isFetched: isPromptsFetched,
  } = useQuery({
    queryKey: promptsQueryKey,
    queryFn: async () => {
      if (!resolvedBrandId || !currentBrand) return [];
      const res = await promptsAPI.getPrompts(
        { ...currentBrand, brandId: resolvedBrandId },
        selectedCountries || [],
        selectedProviders,
        resolvedDays,
        promptDateRange,
      );
      return Array.isArray(res?.clusters)
        ? (res.clusters as PromptCluster[])
        : [];
    },
    enabled: !!resolvedBrandId,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchInterval: (query) => {
      if (query.state.error) return false;
      const data = query.state.data as Array<any> | undefined;
      if (!Array.isArray(data) || !data.length) {
        return EMPTY_ACTIONS_REFETCH_INTERVAL_MS;
      }
      const hasRunningClusters = data.some((cluster: any) =>
        isRunningBoostStatus(cluster?.boostStatus),
      );
      return hasRunningClusters ? EMPTY_ACTIONS_REFETCH_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  usePromptsRealtime({
    brandId: resolvedBrandId,
    countries: selectedCountries,
    providers: selectedProviders,
    days: resolvedDays,
    startDate: promptDateRange?.startDate,
    endDate: promptDateRange?.endDate,
    enabled: !!resolvedBrandId,
    queryKey: promptsQueryKey,
  });

  const clusterActions = useMemo<ClusterAction[]>(() => {
    return Array.isArray(actionablesData?.clusterActions)
      ? actionablesData.clusterActions
      : [];
  }, [actionablesData]);

  const isAllClustersSelected = selectedCluster === ALL_CLUSTERS_ID;
  const isSpecificClusterSelected = Boolean(
    selectedCluster && !isAllClustersSelected,
  );

  const selectedClusterMeta = useMemo<PromptCluster | null>(() => {
    if (!promptsData || !isSpecificClusterSelected) return null;
    return (
      promptsData.find((cluster) => cluster.id === selectedCluster) || null
    );
  }, [isSpecificClusterSelected, promptsData, selectedCluster]);
  const trialAllowedClusterId = useMemo(
    () =>
      (promptsData || []).find((cluster) => cluster?.trial_allowed_cluster_id)
        ?.trial_allowed_cluster_id ||
      Array.from(getRunningClusterIds(promptsData || []))[0] ||
      null,
    [promptsData],
  );
  const isTrialLockedCluster = Boolean(
    planType === "trial" &&
      isSpecificClusterSelected &&
      selectedCluster &&
      trialAllowedClusterId &&
      trialAllowedClusterId !== selectedCluster,
  );
  const trialLockedReason = isTrialLockedCluster
    ? "Trial boost limit reached for this cluster. Upgrade to unlock additional clusters."
    : "";

  const runningClusterIds = useMemo(
    () => getRunningClusterIds(promptsData || []),
    [promptsData],
  );
  const isBoostRunning = Boolean(
    isSpecificClusterSelected &&
      selectedCluster &&
      runningClusterIds.has(selectedCluster),
  );

  const prevBoostStatusRef = React.useRef<string | null>(null);
  useEffect(() => {
    if (!resolvedBrandId) return;
    const currentStatus = selectedClusterMeta?.boostStatus || "";
    const prevStatus = prevBoostStatusRef.current;
    prevBoostStatusRef.current = currentStatus;
    if (!prevStatus || prevStatus === currentStatus) return;
    if (currentStatus === "ready") {
      queryClient.invalidateQueries({
        queryKey: actionsQueryKey,
      });
    }
  }, [
    resolvedBrandId,
    selectedClusterMeta?.boostStatus,
    resolvedDays,
    queryClient,
  ]);

  const promptMap = useMemo(() => {
    const map = new Map<string, string>();
    (promptsData || []).forEach((cluster) => {
      (cluster.prompts || []).forEach((prompt) => {
        map.set(prompt.id, prompt.text);
      });
    });
    return map;
  }, [promptsData]);

  const clusterList = useMemo(() => {
    const clusterCounts = new Map<
      string,
      { allItems: number; completed: number }
    >();
    clusterActions.forEach((action) => {
      const clusterId = action.clusterId || "";
      if (!clusterId) return;
      const entry = clusterCounts.get(clusterId) || {
        allItems: 0,
        completed: 0,
      };
      entry.allItems += 1;
      if (action.status === "resolved" || action.status === "canceled") {
        entry.completed += 1;
      }
      clusterCounts.set(clusterId, entry);
    });

    const rawClusters = (promptsData || [])
      .map((cluster) => {
        const counts = clusterCounts.get(cluster.id) || {
          allItems: 0,
          completed: 0,
        };
        const prompts = Array.isArray(cluster.prompts)
          ? cluster.prompts.filter((prompt: any) => prompt?.is_active !== false)
          : [];
        const activePromptCount = prompts.length;
        const visibility = averageMetric(
          prompts.map((prompt: any) => getPromptMetric(prompt, "visibility")),
        );
        const position = averageMetric(
          prompts.map((prompt: any) => getPromptMetric(prompt, "position")),
        );
        const normalizedBoostStatus = String(cluster.boostStatus || "")
          .trim()
          .toLowerCase();
        const hasBoostHistory =
          Boolean(cluster.boostLastGeneratedAt) ||
          (normalizedBoostStatus.length > 0 &&
            normalizedBoostStatus !== "not_started");
        const isBoosted = counts.allItems > 0 || hasBoostHistory;
        return {
          id: cluster.id,
          title: cluster.title,
          visibility,
          position,
          boostTaskCount:
            typeof cluster.boostTaskCount === "number" &&
            Number.isFinite(cluster.boostTaskCount)
              ? cluster.boostTaskCount
              : null,
          allItems: counts.allItems,
          completed: counts.completed,
          boostStatus: cluster.boostStatus || null,
          boostLastGeneratedAt: cluster.boostLastGeneratedAt || null,
          isBoosted,
          activePromptCount,
        };
      })
      .filter((cluster) => cluster.activePromptCount > 0);

    return sortByStoredClusterOrder(rawClusters, clusterOrderStorageKey);
  }, [clusterActions, clusterOrderStorageKey, promptsData]);

  const selectedClusterListItem = useMemo(() => {
    if (!isSpecificClusterSelected) return null;
    return (
      clusterList.find((cluster) => cluster.id === selectedCluster) || null
    );
  }, [clusterList, isSpecificClusterSelected, selectedCluster]);

  const allClustersListItem = useMemo(() => {
    const activePrompts = (promptsData || []).flatMap((cluster) =>
      Array.isArray(cluster.prompts)
        ? cluster.prompts.filter((prompt: any) => prompt?.is_active !== false)
        : [],
    );
    return {
      visibility: averageMetric(
        activePrompts.map((prompt: any) =>
          getPromptMetric(prompt, "visibility"),
        ),
      ),
      position: averageMetric(
        activePrompts.map((prompt: any) => getPromptMetric(prompt, "position")),
      ),
    };
  }, [promptsData]);

  const metricClusterListItem = isAllClustersSelected
    ? allClustersListItem
    : selectedClusterListItem;
  const selectedClusterVisibility = Number(selectedClusterListItem?.visibility);
  const isSelectedClusterFullyVisible = Boolean(
    isSpecificClusterSelected &&
      Number.isFinite(selectedClusterVisibility) &&
      selectedClusterVisibility >= 100,
  );
  const fullVisibilityBoostDisabledReason =
    "No boost needed. This cluster is already fully covered.";

  const selectedClusterActions = useMemo(() => {
    if (!selectedCluster) return [] as ClusterAction[];
    if (selectedCluster === ALL_CLUSTERS_ID) return clusterActions;
    return clusterActions.filter(
      (action) => (action.clusterId || "") === selectedCluster,
    );
  }, [clusterActions, selectedCluster]);

  const allItems = useMemo<Item[]>(() => {
    return clusterActions.map((action) => {
      const subtasks = Array.isArray(action.subtasks) ? action.subtasks : [];
      const sourceUrlsRaw = Array.isArray(action?.evidence?.source_urls)
        ? action.evidence.source_urls
        : [];
      const sourceUrls = sourceUrlsRaw
        .map((url: any) => String(url || "").trim())
        .filter(Boolean);
      const evidenceUrlsRaw = Array.isArray(action?.evidence?.urls)
        ? action.evidence.urls
        : [];
      const guestPostPlacementUrls = Array.isArray(
        action?.evidence?.guest_post_view?.placements,
      )
        ? action.evidence.guest_post_view.placements
            .map((entry: any) =>
              String(entry?.url || entry?.domain || "").trim(),
            )
            .filter(Boolean)
        : [];
      const communityUrlsRaw = Array.isArray(action?.evidence?.community_urls)
        ? action.evidence.community_urls
        : [];
      const missingCommunityPages = Array.isArray(
        action?.evidence?.missing_community_pages,
      )
        ? action.evidence.missing_community_pages
        : [];
      const missingPublisherPages = Array.isArray(
        action?.evidence?.guest_post_view?.missing_publisher_pages,
      )
        ? action.evidence.guest_post_view.missing_publisher_pages
        : Array.isArray(action?.evidence?.missing_publisher_pages)
          ? action.evidence.missing_publisher_pages
          : [];
      const missingPages = /community/i.test(String(action?.taskType || ""))
        ? missingCommunityPages
        : missingPublisherPages;
      const normalizedTaskType = String(action?.taskType || "")
        .trim()
        .toLowerCase();
      const hasExplicitTaskType = normalizedTaskType.length > 0;
      const taskText = `${action?.title || ""} ${action?.description || ""}`;
      const isCommunityTask = hasExplicitTaskType
        ? normalizedTaskType.includes("community")
        : COMMUNITY_TASK_PATTERN.test(taskText);
      const isYouTubeTask = hasExplicitTaskType
        ? normalizedTaskType.includes("youtube")
        : /\byoutube\b/i.test(taskText);
      const normalizeTaskUrl = (value?: string) => {
        const raw = String(value || "").trim();
        if (!raw) return "";
        if (/^https?:\/\//i.test(raw)) return raw;
        if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(raw)) return `https://${raw}`;
        return raw;
      };
      const communityFallbackUrls = [
        ...communityUrlsRaw,
        ...missingCommunityPages,
        ...sourceUrlsRaw.filter((entry: any) => isCommunitySourceValue(entry)),
        ...evidenceUrlsRaw.filter((entry: any) =>
          isCommunitySourceValue(entry),
        ),
      ]
        .map((entry: any) =>
          normalizeTaskUrl(
            typeof entry === "string"
              ? entry
              : entry?.url || entry?.source_url || entry?.href || entry?.loc,
          ),
        )
        .filter(Boolean);
      const youtubeFallbackUrls = [
        ...sourceUrlsRaw.filter((entry: any) => isYouTubeSourceValue(entry)),
        ...evidenceUrlsRaw.filter((entry: any) => isYouTubeSourceValue(entry)),
        ...communityUrlsRaw.filter((entry: any) => isYouTubeSourceValue(entry)),
      ]
        .map((entry: any) =>
          normalizeTaskUrl(
            typeof entry === "string"
              ? entry
              : entry?.url || entry?.source_url || entry?.href || entry?.loc,
          ),
        )
        .filter(Boolean);
      const resolveUrlAtIndex = (subtask: any, index: number) => {
        const explicit = normalizeTaskUrl(subtask?.url);
        if (explicit) {
          if (isCommunityTask) {
            return isCommunitySourceValue(explicit) ? explicit : "";
          }
          if (isYouTubeTask) {
            return isYouTubeSourceValue(explicit) ? explicit : "";
          }
          return explicit;
        }
        if (isCommunityTask) {
          return normalizeTaskUrl(communityFallbackUrls[index]) || "";
        }
        if (isYouTubeTask) {
          return normalizeTaskUrl(youtubeFallbackUrls[index]) || "";
        }
        const fromGuestPostPlacement = normalizeTaskUrl(
          guestPostPlacementUrls[index],
        );
        if (fromGuestPostPlacement) return fromGuestPostPlacement;
        const fromMissing = normalizeTaskUrl(missingPages[index]?.url);
        if (fromMissing) return fromMissing;
        const fromSource = normalizeTaskUrl(sourceUrls[index]);
        if (fromSource) return fromSource;
        return "";
      };
      const normalizeTaskTitle = (title: string, urlHint: string) => {
        const raw = String(title || "").trim();
        if (!raw) return raw;
        const domainHint = toDomainLabel(urlHint);
        const isOutreachLike = /(outreach|community)/i.test(
          String(action?.taskType || ""),
        );
        if (/^contact$/i.test(raw) && domainHint) {
          return `Contact ${domainHint}`;
        }
        if ((/^reply on$/i.test(raw) || /^reply$/i.test(raw)) && domainHint) {
          return `Reply on ${domainHint}`;
        }
        if (/^contact\s+(.+)$/i.test(raw) && domainHint) {
          return `Contact ${domainHint}`;
        }
        if (/^reply on\s+(.+)$/i.test(raw) && domainHint) {
          return `Reply on ${domainHint}`;
        }
        if (
          isOutreachLike &&
          domainHint &&
          !raw.toLowerCase().includes(domainHint.toLowerCase())
        ) {
          return `${raw} (${domainHint})`;
        }
        const match = raw.match(/^Contact\s+(.+)$/i);
        if (!match) return raw;
        const target = String(match[1] || "").trim();
        if (domainHint) {
          return `Contact ${domainHint}`;
        }
        if (/^https?:\/\//i.test(target)) {
          return `Contact ${toDomainLabel(target) || target}`;
        }
        if (/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(target)) {
          return `Contact ${toDomainLabel(target) || target}`;
        }
        return raw;
      };
      const normalizeTaskDetails = (
        details: string,
        title: string,
        url: string,
      ) => {
        const cleaned = String(details || "")
          .replace(/avg position:\s*[\d.]+\s*/gi, "")
          .replace(/average position:\s*[\d.]+\s*/gi, "")
          .replace(/avg\s*position\s*[\d.]+\s*/gi, "")
          .replace(/average\s*position\s*[\d.]+\s*/gi, "")
          .trim();
        if (cleaned) return cleaned;
        if (/^contact/i.test(title)) {
          return [
            "Reach out to request inclusion or an updated mention on this page.",
            url ? `Target URL: ${url}` : "",
          ]
            .filter(Boolean)
            .join("\n");
        }
        if (/^reply on/i.test(title)) {
          return [
            "Post a concise, value-focused reply on this source.",
            url ? `Target URL: ${url}` : "",
          ]
            .filter(Boolean)
            .join("\n");
        }
        return url ? `Target URL: ${url}` : "";
      };
      const normalizedSubtasks = subtasks.map((subtask: any, index: number) => {
        const resolvedUrl = resolveUrlAtIndex(subtask, index);
        const normalizedTitle = normalizeTaskTitle(
          subtask?.title || "Subtask",
          resolvedUrl,
        );
        return {
          ...subtask,
          ...(resolvedUrl ? { url: resolvedUrl } : {}),
          title: normalizedTitle,
          details: normalizeTaskDetails(
            subtask?.details ?? subtask?.description ?? "",
            normalizedTitle,
            resolvedUrl,
          ),
        };
      });
      const originalSubtasks = Array.isArray(action.original?.subtasks)
        ? action.original.subtasks.map((subtask: any, index: number) => {
            const resolvedUrl = resolveUrlAtIndex(subtask, index);
            const normalizedTitle = normalizeTaskTitle(
              subtask?.title || "Subtask",
              resolvedUrl,
            );
            return {
              ...subtask,
              ...(resolvedUrl ? { url: resolvedUrl } : {}),
              title: normalizedTitle,
              details: normalizeTaskDetails(
                subtask?.details ?? subtask?.description ?? "",
                normalizedTitle,
                resolvedUrl,
              ),
            };
          })
        : null;
      const completedItems = normalizedSubtasks.filter(
        (subtask: any) => subtask?.done,
      ).length;
      const promptIds = action.evidence?.affected_prompt_ids || [];
      const prompts = (Array.isArray(promptIds) ? promptIds : []).map(
        (id: string) => ({
          title: promptMap.get(id) || id,
        }),
      );

      return {
        id: action.id,
        status: toBoardStatus(action.status),
        title: action.title || action.description || "Cluster action",
        description: action.description || "",
        tag: toTaskTypeLabel(action.taskType),
        completedITems: completedItems,
        allItems: normalizedSubtasks.length || 0,
        clusterId: action.clusterId || "",
        priority: toPriority(action.priority),
        effort: "medium",
        impact: normalizeGeneratedCopy(action.impact, { stripPromptIds: true }),
        rationale: normalizeGeneratedCopy(action.rationale),
        original: action.original
          ? {
              title: action.original.title || "",
              description: action.original.description || "",
              rationale: normalizeGeneratedCopy(action.original.rationale),
              impact: normalizeGeneratedCopy(action.original.impact, {
                stripPromptIds: true,
              }),
              tasks: originalSubtasks
                ? originalSubtasks.map((subtask: any) => ({
                    title: subtask?.title || "Subtask",
                    description: subtask?.details || subtask?.description || "",
                    url: subtask?.url || "",
                    done: Boolean(subtask?.done),
                  }))
                : null,
            }
          : null,
        evidence: {
          ...(action.evidence || {}),
        },
        sortIndex: action.sortIndex ?? null,
        actionType: "cluster",
        sourceType: toSourceType(
          String(
            action.evidence?.source_type ||
              action.evidence?.sourceType ||
              action.taskType ||
              "",
          ),
        ),
        difficulty: toPriority(action.priority),
        prompts,
        tasks: normalizedSubtasks.map((subtask: any) => ({
          title: subtask?.title || "Subtask",
          description: subtask?.details || subtask?.description || "",
          url: subtask?.url || "",
          done: Boolean(subtask?.done),
        })),
      };
    });
  }, [clusterActions, promptMap]);

  const sortedAllItems = useMemo<Item[]>(() => {
    const statusOrder: Record<string, number> = {
      "To do": 0,
      "In Progress": 1,
      Completed: 2,
      Dismissed: 3,
    };
    const actionMeta = new Map(
      clusterActions.map((action) => [
        action.id,
        {
          createdAt: action.createdAt
            ? new Date(action.createdAt).getTime()
            : 0,
          sortIndex:
            typeof action.sortIndex === "number" ? action.sortIndex : null,
        },
      ]),
    );
    return allItems.slice().sort((a, b) => {
      const statusDelta =
        (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDelta) return statusDelta;
      const aMeta = actionMeta.get(a.id);
      const bMeta = actionMeta.get(b.id);
      const aIndex = aMeta?.sortIndex;
      const bIndex = bMeta?.sortIndex;
      if (
        aIndex !== null &&
        bIndex !== null &&
        aIndex !== undefined &&
        bIndex !== undefined
      ) {
        if (aIndex !== bIndex) return aIndex - bIndex;
      } else if (aIndex !== null && aIndex !== undefined) {
        return -1;
      } else if (bIndex !== null && bIndex !== undefined) {
        return 1;
      }
      const aCreated = aMeta?.createdAt ?? 0;
      const bCreated = bMeta?.createdAt ?? 0;
      return bCreated - aCreated;
    });
  }, [allItems, clusterActions]);

  const selectedClusterItemIds = useMemo(() => {
    return new Set(selectedClusterActions.map((action) => action.id));
  }, [selectedClusterActions]);

  const selectedClusterBoardItems = useMemo(() => {
    return sortedAllItems.filter((item) => selectedClusterItemIds.has(item.id));
  }, [selectedClusterItemIds, sortedAllItems]);

  const filteredBoardItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return selectedClusterBoardItems.filter((item) => {
      if (
        normalizedSearch &&
        !(item.title || item.description)
          .toLowerCase()
          .includes(normalizedSearch)
      ) {
        return false;
      }

      if (
        clusterFilters?.effort?.length &&
        !clusterFilters.effort.includes(item.effort)
      ) {
        return false;
      }

      if (
        clusterFilters?.priority?.length &&
        !clusterFilters.priority.includes(item.priority)
      ) {
        return false;
      }

      if (
        clusterFilters?.type?.length &&
        !clusterFilters.type.includes(item.tag)
      ) {
        return false;
      }

      return true;
    });
  }, [clusterFilters, search, selectedClusterBoardItems]);
  const visibleBoardItems = useMemo(() => {
    if (isSelectedClusterFullyVisible && isSpecificClusterSelected) return [];
    return filteredBoardItems;
  }, [
    filteredBoardItems,
    isSelectedClusterFullyVisible,
    isSpecificClusterSelected,
  ]);

  const selectedClusterPromptRunStats = useMemo(() => {
    if (!selectedCluster || !isSpecificClusterSelected) {
      return { total: 0, queuedOrRunning: 0, blocked: false };
    }
    const activeCluster = (promptsData || []).find(
      (cluster) => cluster.id === selectedCluster,
    );
    const prompts = Array.isArray(activeCluster?.prompts)
      ? activeCluster.prompts
      : [];
    const activePrompts = prompts.filter(
      (prompt: any) => prompt?.is_active !== false,
    );
    const total = activePrompts.length;
    const queuedOrRunning = activePrompts.filter((prompt: any) =>
      isQueuedOrRunningPrompt(prompt),
    ).length;
    const blocked = total > 0 && queuedOrRunning / total > 0.5;
    return { total, queuedOrRunning, blocked };
  }, [isSpecificClusterSelected, promptsData, selectedCluster]);

  const boostBlockedByPromptRuns = selectedClusterPromptRunStats.blocked;
  const selectedClusterActionCount = selectedClusterActions.length;
  const selectedClusterBoostStatus = String(
    selectedClusterMeta?.boostStatus || "",
  )
    .trim()
    .toLowerCase();
  const selectedClusterBoostIsRunning = isRunningBoostStatus(
    selectedClusterMeta?.boostStatus,
  );
  const selectedClusterBoostedAtMs = toTimestamp(
    selectedClusterMeta?.boostLastGeneratedAt || null,
  );
  const isReadyWithoutActionsWithinGrace = Boolean(
    isSpecificClusterSelected &&
      selectedClusterBoostStatus === "ready" &&
      selectedClusterActionCount === 0 &&
      selectedClusterBoostedAtMs !== null &&
      Date.now() - selectedClusterBoostedAtMs <= READY_WITHOUT_ACTIONS_GRACE_MS,
  );
  const isReadyWithoutActionsSettled = Boolean(
    isSpecificClusterSelected &&
      selectedClusterBoostStatus === "ready" &&
      selectedClusterActionCount === 0 &&
      !isReadyWithoutActionsWithinGrace,
  );
  const isRegenerateAction = selectedClusterBoostStatus === "ready";
  const boostBlockedByCooldown = shouldBlockBoostByCooldown(
    Boolean(selectedClusterMeta?.boostLocked),
    isRegenerateAction,
  );
  const selectedClusterHasActivePrompts =
    isSpecificClusterSelected && selectedClusterPromptRunStats.total > 0;
  const isActionsInitialLoading = Boolean(isLoading && !actionablesData);
  const isPromptsInitialLoading = Boolean(isPromptsLoading && !promptsData);
  const boostBlockedByPromptRunsReason =
    boostBlockedByPromptRuns && !selectedClusterBoostIsRunning
      ? getPromptRunsBlockedMessage(
          selectedClusterPromptRunStats.queuedOrRunning,
          selectedClusterPromptRunStats.total,
        )
      : "";
  const boostBlockedByCooldownReason = boostBlockedByCooldown
    ? getCooldownBlockedMessage(
        selectedClusterMeta?.boostUnlockAt,
        selectedClusterMeta?.boostCooldownDays,
      )
    : "";

  const shouldBlockByPromptRuns =
    boostBlockedByPromptRuns && !selectedClusterBoostIsRunning;
  const showNoTasksPlaceholder = useMemo(() => {
    if (!selectedCluster) return false;
    if (isActionsInitialLoading || isBoostRunning || isBoostSubmitting)
      return false;
    if (isSelectedClusterFullyVisible && isSpecificClusterSelected) return true;
    return selectedClusterActionCount === 0;
  }, [
    isActionsInitialLoading,
    isSelectedClusterFullyVisible,
    isSpecificClusterSelected,
    isBoostRunning,
    isBoostSubmitting,
    selectedCluster,
    selectedClusterActionCount,
  ]);
  const noTasksPlaceholderHint = isReadyWithoutActionsWithinGrace
    ? "Actions are still being prepared for this cluster. This can take a few minutes."
    : isReadyWithoutActionsSettled
      ? "No actions were generated for the latest boost. Try Re-generate to run it again."
      : isTrialLockedCluster
        ? "Trial boost limit reached for this cluster. Upgrade to generate actions."
        : isSelectedClusterFullyVisible
          ? fullVisibilityBoostDisabledReason
          : boostBlockedByCooldown
            ? "No actions were generated yet. Re-generate is currently on cooldown."
            : "Boost this cluster to generate actions based on the sources AI systems cite";

  const canRunBoostAction = Boolean(
    isSpecificClusterSelected &&
      !isBoostRunning &&
      !isBoostSubmitting &&
      !selectedClusterBoostIsRunning &&
      selectedClusterHasActivePrompts &&
      !isReadyWithoutActionsWithinGrace &&
      !isTrialLockedCluster &&
      !isSelectedClusterFullyVisible &&
      !boostBlockedByCooldown &&
      !shouldBlockByPromptRuns,
  );
  const canClickBoostAction = canRunBoostAction || isTrialLockedCluster;
  const boostActionDisabledReason = !isSpecificClusterSelected
    ? "Select a specific cluster to boost."
    : !selectedClusterHasActivePrompts
      ? "This cluster has no active prompts to boost."
      : isReadyWithoutActionsWithinGrace
        ? "Actions are still being prepared for this cluster. Please wait."
        : isTrialLockedCluster
          ? trialLockedReason
          : isSelectedClusterFullyVisible
            ? fullVisibilityBoostDisabledReason
            : boostBlockedByCooldown
              ? boostBlockedByCooldownReason
              : shouldBlockByPromptRuns
                ? boostBlockedByPromptRunsReason
                : "";

  const primaryActionLabel = !isSpecificClusterSelected
    ? "Boost Cluster"
    : isRegenerateAction
      ? "Re-generate"
      : "Boost Cluster";
  const boostedDateLabel = formatBoostedAtLabel(
    selectedClusterMeta?.boostLastGeneratedAt,
  );
  const isClusterBoostInProgress =
    isBoostRunning ||
    isBoostSubmitting ||
    isRunningBoostStatus(selectedClusterMeta?.boostStatus);
  const isSidebarLoading =
    !resolvedBrandId ||
    isActionsInitialLoading ||
    isPromptsInitialLoading ||
    (Boolean(resolvedBrandId) && !isPromptsFetched);
  const allClustersTaskCount = clusterActions.length;
  const shouldShowBoostingStatus = Boolean(
    isSpecificClusterSelected &&
      (isClusterBoostInProgress || isReadyWithoutActionsWithinGrace),
  );
  const shouldShowBoostCooldownStatus = Boolean(
    isSpecificClusterSelected &&
      boostBlockedByCooldown &&
      !isClusterBoostInProgress &&
      !isLoading &&
      !isFetching,
  );
  const shouldShowBoostedStatus = Boolean(
    isSpecificClusterSelected &&
      boostedDateLabel &&
      !isClusterBoostInProgress &&
      !shouldShowBoostCooldownStatus &&
      !isLoading &&
      !isFetching,
  );
  const shouldShowFullVisibilityStatus = Boolean(
    isSpecificClusterSelected && isSelectedClusterFullyVisible,
  );
  const headerStatusTone = shouldShowFullVisibilityStatus
    ? "boosted"
    : shouldShowBoostingStatus
      ? "boosting"
      : shouldShowBoostCooldownStatus
        ? "cooldown"
        : shouldShowBoostedStatus
          ? "boosted"
          : "needs_boost";
  const headerStatusLabel = shouldShowFullVisibilityStatus
    ? fullVisibilityBoostDisabledReason
    : shouldShowBoostingStatus
      ? "Boost is generating actions. This can take a few minutes."
      : shouldShowBoostCooldownStatus
        ? boostBlockedByCooldownReason
        : isAllClustersSelected
          ? "Select a cluster to generate recommended actions"
          : shouldShowBoostedStatus
            ? `This cluster was boosted on ${boostedDateLabel}`
            : "Boost this cluster to generate recommended actions";
  const showBoostAnimation = isSpecificClusterSelected && canRunBoostAction;
  const shouldShowPrimaryActionProcessing = Boolean(
    isBoostRunning || isBoostSubmitting || isReadyWithoutActionsWithinGrace,
  );

  useEffect(() => {
    if (!resolvedBrandId || !selectedCluster || isLoading || isFetching) return;
    const selectedActions = selectedClusterActions;
    const counts = {
      todo: 0,
      in_progress: 0,
      done: 0,
      dismissed: 0,
    };
    selectedActions.forEach((action) => {
      const status = toStatusValue(action.status);
      if (status in counts) {
        (counts as any)[status] += 1;
      }
    });
    const key = `${resolvedBrandId}:${providersKey}:${selectedCluster}:${selectedActions.length}`;
    if (trackedGetCitedViewRef.current === key) return;
    trackedGetCitedViewRef.current = key;
    capturePosthogEvent("cluster_actions_viewed", {
      brand_id: resolvedBrandId,
      cluster_id: selectedCluster,
      has_actions: selectedActions.length > 0,
      actions_count: selectedActions.length,
      actions_by_status: counts,
    });
  }, [
    clusterActions,
    isFetching,
    isLoading,
    providersKey,
    resolvedBrandId,
    selectedCluster,
    selectedClusterActions,
  ]);

  useEffect(() => {
    if (!resolvedBrandId || isLoading || isFetching) return;
    const hasData = Array.isArray(clusterActions) && clusterActions.length > 0;
    const key = `${resolvedBrandId}:${hasData}`;
    if (trackedDashboardViewRef.current === key) return;
    trackedDashboardViewRef.current = key;
    capturePosthogEvent("dashboard_viewed", {
      brand_id: resolvedBrandId,
      dashboard: "get_cited",
      is_first_view: isFirstDashboardView({
        userId: user?.id,
        brandId: resolvedBrandId,
        dashboard: "get_cited",
      }),
      has_data: hasData,
      time_since_onboarding_seconds:
        getTimeSinceBrandOnboardingSeconds(resolvedBrandId) ?? undefined,
    });
  }, [clusterActions, isFetching, isLoading, resolvedBrandId, user?.id]);

  useEffect(() => {
    if (!resolvedBrandId) return;
    const key = `${resolvedBrandId}:${resolvedOrgId || "no-org"}`;
    if (trackedPageViewedRef.current === key) return;
    trackedPageViewedRef.current = key;
    capturePosthogEvent("cluster_actions_page_viewed", {
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
    });
  }, [resolvedBrandId, resolvedOrgId]);

  useEffect(() => {
    if (!resolvedBrandId || !selectedCluster) return;
    capturePosthogEvent("cluster_actions_cluster_selected", {
      cluster_id: selectedCluster,
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
    });
  }, [resolvedBrandId, resolvedOrgId, selectedCluster]);

  useEffect(() => {
    if (!isBoostLimitModalOpen || !resolvedBrandId) return;
    capturePosthogEvent("upgrade_modal_viewed", {
      modal: "trial_boost_limit",
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
    });
  }, [isBoostLimitModalOpen, resolvedBrandId, resolvedOrgId]);

  const handleActionOpened = (item: Item) => {
    if (!resolvedBrandId) return;
    capturePosthogEvent("action_opened", {
      brand_id: resolvedBrandId,
      cluster_id: item.clusterId || null,
      action_id: item.id,
      action_type: toActionType(item.tag),
      source: "cluster_actions",
    });
  };

  useEffect(() => {
    if (!selectedClusterStorageKey) return;
    if (clusterFromSearch) {
      setSelectedCluster(clusterFromSearch);
      return;
    }
    const savedCluster = sessionStorage.getItem(selectedClusterStorageKey);
    if (savedCluster) {
      setSelectedCluster(savedCluster);
    }
  }, [clusterFromSearch, selectedClusterStorageKey]);

  useEffect(() => {
    if (!selectedClusterStorageKey) return;
    if (!selectedCluster) return;
    sessionStorage.setItem(selectedClusterStorageKey, selectedCluster);
  }, [selectedCluster, selectedClusterStorageKey]);

  useEffect(() => {
    if (!clusterList.length) {
      if (selectedCluster !== ALL_CLUSTERS_ID) {
        setSelectedCluster(ALL_CLUSTERS_ID);
      }
      return;
    }
    const exists = selectedCluster
      ? selectedCluster === ALL_CLUSTERS_ID ||
        clusterList.some((cluster) => cluster.id === selectedCluster)
      : false;
    if (!exists) {
      const savedCluster = sessionStorage.getItem(selectedClusterStorageKey);
      const savedExists = savedCluster
        ? savedCluster === ALL_CLUSTERS_ID ||
          clusterList.some((cluster) => cluster.id === savedCluster)
        : false;
      setSelectedCluster(
        savedExists ? (savedCluster as string) : ALL_CLUSTERS_ID,
      );
    }
  }, [clusterList, selectedCluster, selectedClusterStorageKey]);

  const handleBoostOrRegenerate = async (source: "header") => {
    if (!selectedCluster || !isSpecificClusterSelected) {
      toast.error(t("legacy.select.a.cluster.to.boost.prompts.1df1psx"), {
        position: "bottom-right",
      });
      return;
    }
    if (!resolvedBrandId) {
      toast.error(t("legacy.select.a.brand.to.boost.prompts.4092r4"), {
        position: "bottom-right",
      });
      return;
    }
    if (!selectedClusterHasActivePrompts) {
      toast.error(
        t("legacy.this.cluster.has.no.active.prompts.to.boost.9tjl5l"),
        {
          position: "bottom-right",
        },
      );
      return;
    }
    if (isTrialLockedCluster) {
      setIsBoostLimitModalOpen(true);
      capturePosthogEvent("boost_cluster_blocked_trial_limit", {
        requested_cluster_id: selectedCluster,
        trial_boosted_cluster_id: trialAllowedClusterId,
        brand_id: resolvedBrandId,
        org_id: resolvedOrgId,
        source: boostEventSource,
      });
      capturePosthogEvent("cluster_actions_boost_blocked_trial", {
        requested_cluster_id: selectedCluster,
        trial_boosted_cluster_id: trialAllowedClusterId,
      });
      return;
    }
    if (isSelectedClusterFullyVisible) {
      toast.error(fullVisibilityBoostDisabledReason, {
        position: "bottom-right",
      });
      return;
    }
    if (isBoostRunning) {
      toast.error(
        t("legacy.boost.is.already.running.for.this.cluster.1rh5dia"),
        {
          position: "bottom-right",
        },
      );
      return;
    }
    if (boostBlockedByCooldown) {
      toast.error(boostBlockedByCooldownReason, {
        position: "bottom-right",
      });
      return;
    }
    if (boostBlockedByPromptRuns) {
      toast.error(boostBlockedByPromptRunsReason, {
        position: "bottom-right",
      });
      return;
    }

    capturePosthogEvent("boost_cluster_click", {
      cluster_id: selectedCluster,
      cluster_name: selectedClusterMeta?.title || null,
      plan_type: planType,
      brand_id: resolvedBrandId,
      org_id: resolvedOrgId,
      source: boostEventSource,
    });
    capturePosthogEvent(
      isRegenerateAction
        ? "cluster_actions_regenerate_clicked"
        : "cluster_actions_boost_clicked",
      {
        cluster_id: selectedCluster,
        brand_id: resolvedBrandId,
        org_id: resolvedOrgId,
        source,
        plan_type: planType,
      },
    );

    setIsBoostSubmitting(true);
    try {
      const res = await promptsAPI.boostPrompts({
        brandId: resolvedBrandId,
        clusterId: selectedCluster,
        providers:
          selectedProviders && selectedProviders.length
            ? selectedProviders
            : undefined,
        days: resolvedDays,
      });

      if (!res?.ok) {
        const blockedInfo = getTrialBoostBlockedInfo(res);
        if (blockedInfo.blocked) {
          setIsBoostLimitModalOpen(true);
          capturePosthogEvent("boost_cluster_blocked_trial_limit", {
            requested_cluster_id: selectedCluster,
            trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
            brand_id: resolvedBrandId,
            org_id: resolvedOrgId,
            source: boostEventSource,
          });
          capturePosthogEvent("cluster_actions_boost_blocked_trial", {
            requested_cluster_id: selectedCluster,
            trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
          });
          return;
        }
        throw new Error(res?.message || "Failed to start boost.");
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: actionsQueryKey }),
        queryClient.invalidateQueries({ queryKey: promptsQueryKey }),
      ]);
    } catch (err: any) {
      const payload = err?.response?.data || {};
      const blockedInfo = getTrialBoostBlockedInfo(payload);
      if (blockedInfo.blocked) {
        setIsBoostLimitModalOpen(true);
        capturePosthogEvent("boost_cluster_blocked_trial_limit", {
          requested_cluster_id: selectedCluster,
          trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
          brand_id: resolvedBrandId,
          org_id: resolvedOrgId,
          source: boostEventSource,
        });
        capturePosthogEvent("cluster_actions_boost_blocked_trial", {
          requested_cluster_id: selectedCluster,
          trial_boosted_cluster_id: blockedInfo.trialBoostedClusterId,
        });
        return;
      }
      toast.error(
        payload?.message || err?.message || "Failed to start boost.",
        {
          position: "bottom-right",
        },
      );
    } finally {
      setIsBoostSubmitting(false);
    }
  };

  const handleStatusChange = async (
    id: string,
    status: string,
    _?: number | null,
    statusOrderIds?: string[],
    sourceOrderIds?: string[],
    sourceStatus?: string,
  ) => {
    if (!resolvedBrandId) return;
    const nextStatus = mapStatusToApi(status);
    if (!nextStatus) return;
    try {
      const actionsById = new Map(
        clusterActions.map((action) => [action.id, action]),
      );
      const previousAction = actionsById.get(id);
      const fromStatus = sourceStatus
        ? toStatusValue(sourceStatus)
        : toStatusValue(previousAction?.status);
      const toStatus = toStatusValue(status);
      capturePosthogEvent("action_status_changed", {
        brand_id: resolvedBrandId,
        cluster_id: previousAction?.clusterId || null,
        action_id: id,
        action_type: toActionType(previousAction?.taskType),
        from_status: fromStatus,
        to_status: toStatus,
        changed_via: sourceStatus ? "drag_drop" : "menu",
        source: "cluster_actions",
      });

      queryClient.setQueryData(actionsQueryKey, (prev: any) => {
        if (!prev?.clusterActions) return prev;
        return {
          ...prev,
          clusterActions: prev.clusterActions.map((action: any) => {
            const next: any = { ...action };
            if (action.id === id) {
              next.status = nextStatus;
            }
            if (statusOrderIds?.length) {
              const nextIndex = statusOrderIds.indexOf(action.id);
              if (nextIndex !== -1) {
                const clusterId = actionsById.get(action.id)?.clusterId ?? null;
                const positionInCluster = statusOrderIds
                  .filter(
                    (entryId) =>
                      (actionsById.get(entryId)?.clusterId ?? null) ===
                      clusterId,
                  )
                  .indexOf(action.id);
                if (positionInCluster !== -1) {
                  next.sortIndex = positionInCluster;
                }
              }
            }
            if (sourceOrderIds?.length) {
              const nextIndex = sourceOrderIds.indexOf(action.id);
              if (nextIndex !== -1) {
                const clusterId = actionsById.get(action.id)?.clusterId ?? null;
                const positionInCluster = sourceOrderIds
                  .filter(
                    (entryId) =>
                      (actionsById.get(entryId)?.clusterId ?? null) ===
                      clusterId,
                  )
                  .indexOf(action.id);
                if (positionInCluster !== -1) {
                  next.sortIndex = positionInCluster;
                }
              }
            }
            return next;
          }),
        };
      });

      const columns: Record<
        "toAddress" | "inProgress" | "resolved" | "canceled",
        { id: string; sortIndex?: number }[]
      > = {
        toAddress: [],
        inProgress: [],
        resolved: [],
        canceled: [],
      };
      const clusterIndex = new Map<string | null, number>();
      const addWithClusterIndex = (entryId: string) => {
        const clusterId = actionsById.get(entryId)?.clusterId ?? null;
        const nextIndex = clusterIndex.get(clusterId) ?? 0;
        clusterIndex.set(clusterId, nextIndex + 1);
        return { id: entryId, sortIndex: nextIndex };
      };
      if (statusOrderIds?.length) {
        columns[nextStatus].push(...statusOrderIds.map(addWithClusterIndex));
      }
      if (sourceOrderIds?.length && sourceStatus) {
        const sourceStatusApi = mapStatusToApi(sourceStatus);
        if (sourceStatusApi) {
          const sourceClusterIndex = new Map<string | null, number>();
          const addSourceWithClusterIndex = (entryId: string) => {
            const clusterId = actionsById.get(entryId)?.clusterId ?? null;
            const nextIndex = sourceClusterIndex.get(clusterId) ?? 0;
            sourceClusterIndex.set(clusterId, nextIndex + 1);
            return { id: entryId, sortIndex: nextIndex };
          };
          columns[sourceStatusApi].push(
            ...sourceOrderIds.map(addSourceWithClusterIndex),
          );
        }
      }
      if (!columns[nextStatus].length) {
        columns[nextStatus].push({ id, sortIndex: 0 });
      }

      await visabilityAPI.reorderActionables({
        brandId: resolvedBrandId,
        columns,
      });
    } catch {
      // ignore update errors for now
    }
  };

  const handlePriorityChange = async (id: string, priority: string) => {
    if (!resolvedBrandId) return;
    const nextPriority = mapPriorityToApi(priority);
    if (!nextPriority) return;
    try {
      await visabilityAPI.updateActionableById({
        id,
        brandId: resolvedBrandId,
        priority: nextPriority,
      });
      queryClient.invalidateQueries({
        queryKey: actionsQueryKey,
      });
    } catch {
      // ignore update errors for now
    }
  };

  const handleSubtasksChange = async (id: string, tasks: any[]) => {
    if (!resolvedBrandId) return;
    const normalizedTasks = tasks.map((task) => ({
      ...task,
      details: task.details ?? task.description ?? "",
    }));
    try {
      queryClient.setQueryData(actionsQueryKey, (prev: any) => {
        if (!prev?.clusterActions) return prev;
        return {
          ...prev,
          clusterActions: prev.clusterActions.map((action: any) =>
            action.id === id
              ? { ...action, subtasks: normalizedTasks }
              : action,
          ),
        };
      });
      await visabilityAPI.updateActionableById({
        id,
        brandId: resolvedBrandId,
        subtasks: normalizedTasks,
      });
    } catch {
      // ignore update errors for now
    }
  };

  return (
    <>
      <div className="flex h-full min-h-0 flex-col gap-3 overflow-x-hidden">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-start">
          <div className="grow text-sm text-[#737373]">
            <LocalizedText id="legacy.actions.to.improve.visibility.for.specific.prompts.based.1798rou" />
          </div>

          <div className="relative h-fit w-full max-w-sm lg:w-[320px] lg:max-w-[320px]">
            <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("legacy.search.14mmb9h")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="min-w-[200px] max-w-sm w-full pl-8"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer px-0 text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="flex h-full min-h-0 overflow-hidden">
          <div className="mr-3 flex h-full max-w-[264px] min-w-[264px] flex-shrink-0 flex-col overflow-hidden rounded-[8px] border border-[#E5E5E5] bg-white">
            {clusterList.length ? (
              <div className="flex min-h-0 flex-col">
                <div className="border-b border-[#E5E5E5] p-2">
                  <div
                    data-testid="cluster-item-all"
                    onClick={() => setSelectedCluster(ALL_CLUSTERS_ID)}
                    className={`flex h-9 cursor-pointer items-center justify-between rounded-[8px] px-2 py-2 ${
                      isAllClustersSelected
                        ? "bg-[#F5F5F5]"
                        : "hover:bg-[#FAFAFA]"
                    }`}
                  >
                    <div className="truncate text-sm font-medium leading-5 text-[#0A0A0A]">
                      <LocalizedText id="legacy.all.clusters.1ch60zh" />
                    </div>
                    <div className="ml-2 whitespace-nowrap text-sm leading-5 text-[#737373]">
                      {allClustersTaskCount}
                    </div>
                  </div>
                </div>
                <div className="flex min-h-0 flex-col overflow-y-auto p-2">
                  {clusterList.map((item) => {
                    const isSelected = selectedCluster === item.id;
                    const progressText =
                      item.allItems > 0
                        ? `${item.completed}/${item.allItems}`
                        : item.boostTaskCount && item.boostTaskCount > 0
                          ? `0/${item.boostTaskCount}`
                          : null;
                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedCluster(item.id)}
                        className={`flex h-9 cursor-pointer items-center justify-between rounded-[8px] px-2 py-2 ${
                          isSelected ? "bg-[#F5F5F5]" : "hover:bg-[#FAFAFA]"
                        }`}
                      >
                        <div className="min-w-0 truncate text-sm font-medium leading-5 text-[#0A0A0A]">
                          {item.title}
                        </div>
                        {runningClusterIds.has(item.id) ? (
                          <Spinner
                            className="ml-2 text-[#FF5E00]"
                            variant="circle"
                            size={14}
                          />
                        ) : progressText ? (
                          <div className="ml-2 whitespace-nowrap text-sm leading-5 text-[#737373]">
                            {progressText}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : isSidebarLoading ? (
              <div className="flex h-full min-h-[120px] w-full items-center justify-center p-1">
                <Spinner
                  className="text-[#FF5E00]"
                  variant="circle-filled"
                  size={25}
                />
              </div>
            ) : (
              <div className="flex grow">
                <div className="my-auto flex flex-col items-center gap-3">
                  <div className="rounded-md bg-[#F5F5F5] p-2">
                    <Info size={24} strokeWidth={1.5} />
                  </div>
                  <div className="text-lg font-medium text-[#0A0A0A]">
                    <LocalizedText id="legacy.no.clusters.yet.1nsyylx" />
                  </div>
                  <div className="text-center text-sm leading-5 text-[#737373]">
                    <LocalizedText id="legacy.create.and.manage.clusters.on.prompts.to.generate.1vper0t" />
                  </div>
                  <Button
                    onClick={() => {
                      const brandSearch = resolvedBrandId
                        ? `?brandId=${resolvedBrandId}`
                        : "";
                      navigate(`/dashboard/prompts${brandSearch}`);
                    }}
                    className="mb-2 w-full cursor-pointer border bg-[#FF5E00] text-white hover:bg-[#ff8138] hover:text-white"
                  >
                    <LocalizedText id="legacy.open.prompts.v9c5ba" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-1 flex-col gap-2.5 overflow-hidden">
            {selectedClusterMeta || isAllClustersSelected ? (
              <div className="py-1">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="h-8 px-2 py-0">
                      <Eye></Eye>{" "}
                      <LocalizedText id="legacy.visibility.s925mr" />{" "}
                      <div className="bg-[#FF5E001A] text-[#FF5E00] p-1 rounded-md">
                        {formatAvgMetric(
                          metricClusterListItem?.visibility ?? null,
                          "%",
                        )}
                      </div>
                    </Badge>
                    <Badge variant="outline" className="h-8 px-2 py-0">
                      <ChartNoAxesColumnIncreasing />
                      <LocalizedText id="legacy.position.1k9t5h8" />{" "}
                      <div className="bg-[#FF5E001A] text-[#FF5E00] p-1 rounded-md">
                        {formatAvgMetric(
                          metricClusterListItem?.position ?? null,
                        )}
                      </div>
                    </Badge>
                  </div>

                  <div className="ml-auto flex min-w-0 items-center justify-end gap-1 2xl:w-auto">
                    <div
                      className={`inline-flex max-w-full items-center gap-1 truncate rounded-md px-3 py-1.5 text-sm 2xl:max-w-[420px] ${
                        headerStatusTone === "boosted"
                          ? "bg-[#E9F7EF] text-[#1E7A3C]"
                          : "bg-[#FFF4ED] text-[#A63E00]"
                      }`}
                    >
                      {headerStatusTone === "boosted" ? (
                        <BadgeCheck size={14} />
                      ) : (
                        <Info size={14} />
                      )}
                      <span className="truncate">{headerStatusLabel}</span>
                    </div>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={
                            showBoostAnimation
                              ? "relative inline-flex max-w-full shrink-0"
                              : "inline-flex max-w-full shrink-0"
                          }
                        >
                          {showBoostAnimation ? (
                            <svg
                              className="pointer-events-none absolute -inset-1 h-[calc(100%+8px)] w-[calc(100%+8px)]"
                              viewBox="0 0 100 40"
                              preserveAspectRatio="none"
                            >
                              <rect
                                x="2"
                                y="2"
                                width="96"
                                height="36"
                                rx="6"
                                fill="none"
                                stroke="#FF5E00"
                                strokeWidth="2"
                                strokeDasharray="20 234"
                                strokeLinecap="round"
                                style={{ filter: "blur(1.5px)" }}
                                className="border-loader"
                              />
                              <rect
                                x="2"
                                y="2"
                                width="96"
                                height="36"
                                rx="6"
                                fill="none"
                                stroke="#FF5E00"
                                strokeWidth="2"
                                strokeDasharray="20 234"
                                strokeLinecap="round"
                                className="border-loader"
                              />
                            </svg>
                          ) : null}
                          <Button
                            className={`h-8 cursor-pointer bg-[#0A0A0A] px-4 text-[#FAFAFA] hover:bg-[#171717] ${
                              showBoostAnimation ? "relative z-10" : ""
                            }`}
                            disabled={!canClickBoostAction}
                            onClick={() => handleBoostOrRegenerate("header")}
                          >
                            {shouldShowPrimaryActionProcessing ? (
                              <Spinner className="text-[#FAFAFA]" size={16} />
                            ) : primaryActionLabel === "Re-generate" ? (
                              <img
                                src={repeatIcon}
                                alt=""
                                className="h-4 w-4 brightness-0 invert"
                              />
                            ) : null}
                            {shouldShowPrimaryActionProcessing
                              ? "Processing"
                              : primaryActionLabel}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {!canClickBoostAction &&
                      !shouldShowPrimaryActionProcessing &&
                      boostActionDisabledReason ? (
                        <TooltipContent
                          side="top"
                          className="max-w-[260px] whitespace-normal break-words text-center"
                        >
                          {boostActionDisabledReason}
                        </TooltipContent>
                      ) : null}
                    </Tooltip>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-md border p-4 text-sm text-[#737373]">
                <LocalizedText id="legacy.select.a.cluster.to.see.and.manage.actions.kmuip3" />
              </div>
            )}

            <div className="relative min-h-0 flex-1 max-w-full overflow-auto overscroll-contain border-t border-[#E5E5E5] pt-3">
              <ClustersDnDColumn
                key={`cluster-board-${selectedCluster || "none"}-${isSelectedClusterFullyVisible ? "full" : "normal"}`}
                loading={
                  isBoostRunning || isBoostSubmitting || isActionsInitialLoading
                }
                showBoostPrompts={true}
                INITIAL_ITEMS={visibleBoardItems}
                onStatusChange={handleStatusChange}
                onPriorityChange={handlePriorityChange}
                showNoTasksPlaceholder={showNoTasksPlaceholder}
                onSubtasksChange={handleSubtasksChange}
                onItemOpened={handleActionOpened}
              ></ClustersDnDColumn>
              {showNoTasksPlaceholder && selectedClusterMeta ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-transparent">
                  <div className="flex h-[212px] w-full max-w-[860px] flex-col items-center justify-center gap-4 rounded-[8px] p-12">
                    <div className="flex w-full max-w-[764px] flex-col items-center justify-center gap-2">
                      <div className="flex h-[52px] w-[52px] items-center justify-center rounded-[12px] bg-[#F5F5F5] p-[10px]">
                        <Package size={32} strokeWidth={2} />
                      </div>
                      <div className="flex w-full max-w-[764px] flex-col items-center justify-center">
                        <div className="w-full text-center text-[18px] font-medium leading-7 text-[#0A0A0A]">
                          <LocalizedText id="legacy.no.actions.yet.for.this.cluster.qd28fq" />
                        </div>
                        <div className="w-full text-center text-sm leading-5 text-[#737373]">
                          {noTasksPlaceholderHint}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={isBoostLimitModalOpen}
        onOpenChange={setIsBoostLimitModalOpen}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[425px] gap-4 border-[#E5E5E5] p-4 sm:max-w-[425px] sm:p-4"
        >
          <DialogHeader className="items-center gap-2 text-center sm:text-center">
            <TriangleAlert size={28} className="text-[#FF5E00]" />
            <DialogTitle className="text-xl leading-7 font-semibold">
              <LocalizedText id="legacy.boost.limit.reached.6n7wi1" />
            </DialogTitle>
            <DialogDescription className="mx-auto max-w-[375px] text-center leading-6">
              <span className="block">
                <LocalizedText id="legacy.your.trial.includes.boost.actions.for.1.cluster.10li0v" />
              </span>
              <span className="block">
                <LocalizedText id="legacy.upgrade.to.boost.more.clusters.and.unlock.full.6gk8jp" />
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-hidden rounded-lg border border-[#E5E5E5]">
            <div className="divide-y divide-[#E5E5E5] px-4 py-3 sm:px-6">
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <ChartNoAxesColumnIncreasing size={16} />
                  <LocalizedText id="legacy.boost.unlimited.clusters.1ayeniu" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <ScanEye size={16} />
                  <LocalizedText id="legacy.full.visibility.analytics.19jp1i" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
              <div className="flex items-center justify-between py-3 text-sm font-medium text-[#0A0A0A]">
                <span className="inline-flex items-center gap-2">
                  <Zap size={16} />
                  <LocalizedText id="legacy.higher.usage.limits.1tpejxb" />
                </span>
                <BadgeCheck className="text-[#16A34A]" size={18} />
              </div>
            </div>
            <div className="border-t border-[#E5E5E5] px-4 py-1 text-center text-xs text-[#737373]">
              <LocalizedText id="legacy.and.more.features.h73j0y" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              className="cursor-pointer bg-[#FF5E00] text-white hover:bg-[#ff8138]"
              onClick={() => {
                const brandSearch = resolvedBrandId
                  ? `?brandId=${resolvedBrandId}`
                  : "";
                capturePosthogEvent("upgrade_clicked", {
                  modal: "trial_boost_limit",
                  brand_id: resolvedBrandId,
                  org_id: resolvedOrgId,
                });
                setIsBoostLimitModalOpen(false);
                navigate(`/dashboard/plans-pricing${brandSearch}`);
              }}
            >
              <LocalizedText id="legacy.see.plans.and.upgrade.11ooxj4" />
            </Button>
            <Button
              variant="outline"
              className="cursor-pointer w-fit self-center"
              onClick={() => setIsBoostLimitModalOpen(false)}
            >
              <LocalizedText id="legacy.not.now.1f5o0g6" />
            </Button>
          </div>

          <div className="text-center text-xs text-[#737373]">
            <LocalizedText id="legacy.need.help.or.a.custom.plan.16lhm8t" />{" "}
            <button
              className="underline underline-offset-2 text-[#0A0A0A]"
              onClick={() => {
                setIsBoostLimitModalOpen(false);
                navigate("/dashboard/support");
              }}
            >
              <LocalizedText id="legacy.contact.us.14c3h4z" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
